import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';
import type { LoginInput, MeResponse, UserSummary } from '@displaycms/contracts';

import { apiGet, apiPost, ApiError } from '@/api/client';
import { hasExplicitLangPreference } from '@/i18n/i18n';

/**
 * Authentication state machine for the admin SPA.
 *
 *   loading       -- initial /auth/me probe in flight
 *   authenticated -- /auth/me succeeded; `user` + `permissions` populated
 *   unauthenticated -- /auth/me returned 401 (or login/logout transitioned us
 *                      back here)
 *
 * The provider mounts once at the SPA root. The session cookie is httpOnly,
 * so we cannot read it from JS; the only way to know whether we're signed in
 * is to call /auth/me and observe the response.
 */
export type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated';

export interface AuthContextValue {
  status: AuthStatus;
  currentUser: UserSummary | null;
  permissions: readonly string[];
  hasPermission: (name: string) => boolean;
  login: (input: LoginInput) => Promise<void>;
  logout: () => Promise<void>;
  /** Refetches /auth/me. Exposed for tests and post-mutation refresh. */
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps): JSX.Element {
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [currentUser, setCurrentUser] = useState<UserSummary | null>(null);
  const [permissions, setPermissions] = useState<readonly string[]>([]);
  const { i18n } = useTranslation();

  // Guards against state updates after unmount; tests in particular re-render
  // the provider frequently.
  const aliveRef = useRef(true);
  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);

  const applyMe = useCallback(
    (me: MeResponse | null): void => {
      if (!aliveRef.current) return;
      if (me === null) {
        setCurrentUser(null);
        setPermissions([]);
        setStatus('unauthenticated');
        return;
      }
      setCurrentUser(me.user);
      setPermissions(me.permissions);
      setStatus('authenticated');

      // Spec §6: auth response sets the initial lang, but the user's explicit
      // in-app preference always wins (so flipping the switcher to EN doesn't
      // revert on next /me).
      if (!hasExplicitLangPreference() && me.user.locale && i18n.language !== me.user.locale) {
        void i18n.changeLanguage(me.user.locale);
      }
    },
    [i18n],
  );

  const refresh = useCallback(async (): Promise<void> => {
    try {
      const me = await apiGet<MeResponse>('/v1/auth/me');
      applyMe(me);
    } catch (err) {
      if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
        applyMe(null);
        return;
      }
      // Network / 5xx — treat as unauthenticated for the purposes of guarding
      // routes, but surface nothing to the user (RequireAuth will redirect to
      // /login).
      applyMe(null);
    }
  }, [applyMe]);

  // Bootstrap on mount.
  useEffect(() => {
    void refresh();
  }, [refresh]);

  const login = useCallback(
    async (input: LoginInput): Promise<void> => {
      // The server sets the httpOnly cookies on success. We don't need the
      // response body — we re-fetch /me to canonicalize state (and to get the
      // computed permissions list).
      await apiPost<unknown>('/v1/auth/login', input);
      await refresh();
    },
    [refresh],
  );

  const logout = useCallback(async (): Promise<void> => {
    try {
      await apiPost<unknown>('/v1/auth/logout');
    } catch {
      // Even if the server logout fails, drop client state.
    }
    applyMe(null);
  }, [applyMe]);

  const hasPermission = useCallback(
    (name: string): boolean => permissions.includes(name),
    [permissions],
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      currentUser,
      permissions,
      hasPermission,
      login,
      logout,
      refresh,
    }),
    [status, currentUser, permissions, hasPermission, login, logout, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an <AuthProvider>');
  }
  return ctx;
}
